import { useRef, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import {
  submitAmbassadorApplication,
  type CkdStatus,
} from '@/lib/ambassador-application';
import { pushAnalyticsEvent } from '@/lib/analytics';

interface AmbassadorApplicationFormProps {
  source: string;
  className?: string;
}

type FormState = 'idle' | 'submitting' | 'success';

const CKD_STATUS_OPTIONS: Array<{ value: CkdStatus; label: string }> = [
  { value: 'patient', label: 'CKD Patient' },
  { value: 'survivor', label: 'CKD Survivor' },
  { value: 'caregiver', label: 'Caregiver / Family Member' },
];

export function AmbassadorApplicationForm({
  source,
  className = '',
}: Readonly<AmbassadorApplicationFormProps>): JSX.Element {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [ckdStatus, setCkdStatus] = useState<CkdStatus>('patient');
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialTiktok, setSocialTiktok] = useState('');
  const [socialYoutube, setSocialYoutube] = useState('');
  const [socialInstagram, setSocialInstagram] = useState('');
  const [storySnippet, setStorySnippet] = useState('');
  const [languages, setLanguages] = useState('');
  const [consentAcknowledged, setConsentAcknowledged] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState>('idle');
  const [honeypot, setHoneypot] = useState('');
  const mountTime = useRef(Date.now());

  const COOLDOWN_KEY = 'gsgo_ambassador_apply_last_ts';
  const COOLDOWN_MS = 120_000;

  const validate = (): string | null => {
    if (fullName.trim().length < 2 || fullName.trim().length > 120) {
      return 'Please enter your full name (2-120 characters).';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return 'Please enter a valid email address.';
    }
    if (!country.trim() || country.trim().length > 100) {
      return 'Please enter your country.';
    }
    if (storySnippet.trim().length < 50 || storySnippet.trim().length > 2000) {
      return 'Please share your story in 50 to 2000 characters.';
    }
    if (!consentAcknowledged) {
      return 'Please confirm your consent before submitting.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrorMessage(null);

    // Honeypot for bots.
    if (honeypot.trim()) {
      setFormState('success');
      return;
    }

    // Reject instant bot submissions.
    if (Date.now() - mountTime.current < 1500) {
      setErrorMessage('Please review your inputs and try again.');
      return;
    }

    // Simple browser-level cooldown against rapid repeat submissions.
    try {
      const last = localStorage.getItem(COOLDOWN_KEY);
      if (last && Date.now() - Number(last) < COOLDOWN_MS) {
        setErrorMessage('You just submitted recently. Please wait a bit before trying again.');
        return;
      }
    } catch {
      // localStorage unavailable; continue.
    }

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setFormState('submitting');
    try {
      await submitAmbassadorApplication({
        fullName,
        email,
        phone,
        country,
        ckdStatus,
        socialFacebook,
        socialTiktok,
        socialYoutube,
        socialInstagram,
        storySnippet,
        languages,
        consentAcknowledged,
        source,
      });
      setFormState('success');
      pushAnalyticsEvent({
        event: 'ambassador_apply_submit',
        source,
        ckd_status: ckdStatus,
      });
      try {
        localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
      } catch {
        // noop
      }
    } catch (err) {
      console.error(
        '[AmbassadorApplicationForm] Failed to submit ambassador application:',
        err
      );
      setFormState('idle');
      setErrorMessage('Something went wrong. Please try again in a moment.');
    }
  };

  if (formState === 'success') {
    return (
      <div
        className={`rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center ${className}`}
      >
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircle2 className="text-emerald-600" size={22} />
        </div>
        <p className="text-lg font-semibold text-emerald-900">Application submitted</p>
        <p className="mt-1 text-sm text-emerald-800">
          Thank you for applying. A confirmation email is on its way to the address you provided.
          Our team will review your submission and contact you with next steps.
        </p>
      </div>
    );
  }

  return (
    <form
      id="ambassador-form"
      onSubmit={handleSubmit}
      className={`space-y-4 ${className}`}
      noValidate
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          opacity: 0,
        }}
      >
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Full name"
          required
          maxLength={120}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          required
          maxLength={254}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone (optional)"
          maxLength={40}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
        <input
          type="text"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="Country"
          required
          maxLength={100}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
      </div>

      <select
        value={ckdStatus}
        onChange={(e) => setCkdStatus(e.target.value as CkdStatus)}
        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
        disabled={formState === 'submitting'}
      >
        {CKD_STATUS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="text"
          value={socialFacebook}
          onChange={(e) => setSocialFacebook(e.target.value)}
          placeholder="Facebook (optional)"
          maxLength={200}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
        <input
          type="text"
          value={socialTiktok}
          onChange={(e) => setSocialTiktok(e.target.value)}
          placeholder="TikTok (optional)"
          maxLength={200}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="text"
          value={socialYoutube}
          onChange={(e) => setSocialYoutube(e.target.value)}
          placeholder="YouTube (optional)"
          maxLength={200}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
        <input
          type="text"
          value={socialInstagram}
          onChange={(e) => setSocialInstagram(e.target.value)}
          placeholder="Instagram (optional)"
          maxLength={200}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
      </div>

      <input
        type="text"
        value={languages}
        onChange={(e) => setLanguages(e.target.value)}
        placeholder="Languages spoken (optional)"
        maxLength={200}
        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
        disabled={formState === 'submitting'}
      />

      <textarea
        value={storySnippet}
        onChange={(e) => setStorySnippet(e.target.value)}
        placeholder="Share your story and why you want to become an ambassador (50+ characters)"
        required
        minLength={50}
        maxLength={2000}
        rows={5}
        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
        disabled={formState === 'submitting'}
      />

      <label className="flex items-start gap-3 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={consentAcknowledged}
          onChange={(e) => setConsentAcknowledged(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-[#148F77] focus:ring-[#148F77]"
          disabled={formState === 'submitting'}
        />
        <span>
          I consent to GSGO storing my submission and contacting me about the CKD Warrior Ambassador program.
        </span>
      </label>

      {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}

      <button
        type="submit"
        disabled={formState === 'submitting'}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#148F77] px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#148F77] focus:ring-offset-2 disabled:opacity-50"
      >
        {formState === 'submitting' ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            <span>Submitting…</span>
          </>
        ) : (
          <span>Apply to become a warrior</span>
        )}
      </button>
    </form>
  );
}
